// Prompt builders for the BAFA wizard. Each function returns the exact text
// that gets sent to the agent over the WebSocket — kept in lock-step with the
// legacy `web/public/app.js` so server-side prompt files
// (`agents/bafa/input/prompts/*`) keep matching.

import { FIXED_VALUES, type BafaReportType, type Form1State, type Form2State } from "./state";

export function buildStep1Prompt(form1: Form1State, slugFromUrl: string): string {
  const { reportType, websiteUrl, kernproblem, transkripte, muendlicheAbsprachen } = form1;
  const lines: string[] = [
    "Schritt 1: Unternehmensdaten ermitteln und Run-Directory anlegen.",
    "",
    "**Priorität:** Nutze zuerst die unten stehenden Rohdaten (Transkripte, Notizen, mündliche Absprachen). Darin stehen oft bereits alle Unternehmensdaten (Firmenname, Branche, Inhaber, Standort, USt-ID, etc.). NorthData und Web-Recherche nur bei Bedarf, um Lücken zu füllen.",
    "",
    websiteUrl
      ? "Slug für Run-Directory: " + slugFromUrl + " (aus Website-URL abgeleitet)."
      : "Slug für Run-Directory: " + slugFromUrl + " (kein Website-URL angegeben – Slug aus Kontext oder Timestamp).",
    "",
    "1. Lege output/{YYYYMMDD}_{slug}/data/ an (slug z. B. " + slugFromUrl + " oder aus Firmennamen).",
    "2. Speichere die Rohdaten in {run_dir}/data/transcript.md (Transkripte) und ggf. {run_dir}/data/muendliche_absprachen.md.",
    "3. Extrahiere Unternehmensdaten aus den Rohdaten und schreibe {run_dir}/data/company_info.json.",
    `   Format: {"firmenname": "...", "branche": "...", "inhaber": "...", "standort": "...", "ust_id": null, "rechtsform": null, "mitarbeiter": null, "gruendungsjahr": null, "report_type": "${reportType}", "beratungsanlass": "..."}`,
    websiteUrl
      ? "4. Scrape die Website " + websiteUrl + " nach {run_dir}/data/website.md (falls noch nicht vorhanden)."
      : "4. Kein Website-URL angegeben – überspringe Website-Scraping. Unternehmensdaten ausschließlich aus Rohdaten und NorthData/Web-Recherche ermitteln.",
    "5. NorthData/Web-Recherche nur wenn nötig, um fehlende Felder zu ergänzen.",
    "",
    "Kontext: Berichtstyp " + reportType + ". Kernproblem/Anlass: " + (kernproblem || "(noch nicht angegeben)") + ".",
  ];
  if (transkripte) lines.push("", "--- Rohdaten: Transkripte / Notizen / PDFs ---", "", transkripte);
  if (muendlicheAbsprachen) lines.push("", "--- Rohdaten: Mündliche Absprachen ---", "", muendlicheAbsprachen);
  return lines.join("\n");
}

export function buildStep2Prompt(
  form1: Form1State,
  form2: Form2State,
  currentCompanySlug: string,
  slugFromUrl: string | null,
): string {
  const reportType = (form1.reportType || "lead_easy") as BafaReportType;
  const fixed = FIXED_VALUES[reportType] ?? FIXED_VALUES.lead_easy;
  const lines: string[] = [
    "Neuen Bericht anlegen.",
    "",
    "Berichtstyp: " + reportType,
    "Firmenname: " + form2.firmenname,
    "company_slug: " + currentCompanySlug,
  ];
  const add = (label: string, value: string) => {
    if (value) lines.push(label + ": " + value);
  };
  add("Website-URL", form1.websiteUrl);
  add("Branche/Geschäftsmodell", form2.branche);
  add("Inhaber/Geschäftsführung", form2.inhaber);
  add("Standort", form2.standort);
  const mit = form1.mitarbeitende;
  if (mit && mit !== "nicht_angegeben") add("Mitarbeiterzahl", mit);
  add("Kernproblem / Anlass der Beratung", form1.kernproblem);
  if (form1.zeitraumStart || form1.zeitraumEnd) {
    lines.push(
      "Beratungszeitraum: " +
        (form1.zeitraumStart || "?") +
        " bis " +
        (form1.zeitraumEnd || "?"),
    );
  }
  if (fixed.beratertage != null) {
    lines.push(
      "Leistungsumfang: " +
        fixed.beratertage +
        " Beratertage, " +
        fixed.stunden +
        " Stunden (fix für Typ " +
        reportType +
        ").",
    );
  }
  add("Transkripte/Notizen/PDFs", form1.transkripte);
  add("Mündliche Absprachen", form1.muendlicheAbsprachen);
  lines.push("");
  lines.push(
    "WICHTIG: Es existiert bereits ein Run-Directory in output/ (endet auf _" +
      currentCompanySlug +
      " oder _" +
      (slugFromUrl || currentCompanySlug) +
      "). Diesen Ordner wiederverwenden! Keinen neuen anlegen.",
  );
  lines.push("Rohdaten (Transkripte, Absprachen, Angaben) in {run_dir}/data/ ablegen.");
  lines.push(
    "Fragebogen ausfüllen: input/prompts/questionnaire_fill.md befolgen, " +
      "Rohdaten in {run_dir}/data/ nutzen, " +
      "Ergebnis in {run_dir}/data/questionnaire.json schreiben.",
  );
  return lines.join("\n");
}

export function buildStep3Prompt(currentCompanySlug: string): string {
  return (
    "Bericht generieren für " +
    currentCompanySlug +
    ". Anschließend Bericht prüfen. Anschließend Bericht zusammensetzen."
  );
}
